import { abortableFetch, flatten } from "util";

import queryString from "query-string";
import { v4 } from "uuid";

import { REFRESHING_FILTERS, filterCardlists } from "store/additionalFilters";
import { addCards } from "store/cards";
import { DOMAIN } from "store/constants";
import { toast } from "store/toasts";

const DONE_LOADING = "pagedata/DONE_LOADING";
const FETCHING_MORE = "pagedata/FETCHING_MORE";
const FETCHING_MORE_SUCCESS = "pagedata/FETCHING_MORE_SUCCESS";
const FETCHING_MORE_ERROR = "pagedata/FETCHING_MORE_ERROR";
const FETCHING_MORE_ABORT = "pagedata/FETCHING_MORE_ABORT";
const FETCHING_PAGEDATA = "pagedata/FETCHING_PAGEDATA";
export const FETCHING_PAGEDATA_SUCCESS = "pagedata/FETCHING_PAGEDATA_SUCCESS";
const FETCHING_PAGEDATA_ERROR = "pagedata/FETCHING_PAGEDATA_ERROR";
const FETCHING_PAGEDATA_TIMEDOUT_FILTER = "pagedata/FETCHING_PAGEDATA_TIMEDOUT_FILTER";
const FETCHING_PAGEDATA_ABORT = "pagedata/FETCHING_PAGEDATA_ABORT";
const SET_FILTERS = "pagedata/SET_FILTERS";
const SET_IS_INITIALIZING = "pagedata/SET_IS_INITIALIZING";

export const initialFilters = { count: [], name: [] };

const initialState = {
  data: null,
  error: "",
  fetches: [],
  filterError: "",
  filters: initialFilters,
  isInitializing: true,
  isLoading: false,
  isLoadingMore: false,
  location: null,
  prevData: null,
  prevLocation: null,
  scrollKey: null,
};

const pagedata = (state = initialState, action) => {
  switch (action.type) {
    case DONE_LOADING:
      return { ...state, isLoading: false };
    case FETCHING_MORE:
      return { ...state, isLoadingMore: true };
    case FETCHING_MORE_SUCCESS:
      return {
        ...state,
        data: {
          ...state.data,
          container: {
            ...state.data.container,
            json_dict: {
              ...state.data.container.json_dict,
              ...action.json_dict,
            },
          },
        },
        isLoadingMore: false,
      };
    case FETCHING_MORE_ERROR:
      return { ...state };
    case FETCHING_PAGEDATA:
      return {
        ...state,
        error: "",
        fetches: [...state.fetches, action.fetch],
        filterError: "",
        isLoading: true,
      };
    case FETCHING_PAGEDATA_SUCCESS: {
      return {
        ...state,
        data: action.data,
        fetches: [],
        isInitializing: false,
        isLoading: false,
      };
    }
    case FETCHING_PAGEDATA_ERROR:
      return {
        ...state,
        error: action.error,
        fetches: [],
        isInitializing: false,
        isLoading: false,
      };
    case FETCHING_PAGEDATA_TIMEDOUT_FILTER:
      return {
        ...state,
        fetches: [],
        filterError: "Query timed out, please use stricter filters or try again later",
        isInitializing: false,
        isLoading: false,
      };
    case FETCHING_PAGEDATA_ABORT:
      return {
        ...state,
        fetches: [],
      };
    case "LOCATION_CHANGE": {
      const { location, prevLocation } = action;

      const [dir, page] = location.pathname.slice(1).split("/");
      const [prevDir, prevPage] = prevLocation.pathname.slice(1).split("/");

      // Keep filters if doing a filter or going between /commanders/... and /decks/...
      const keepFilters =
        (location.pathname === prevLocation?.pathname && location.search !== prevLocation?.search) ||
        (dir !== prevDir && [dir, prevDir].every((d) => ["commanders", "decks"].includes(d)));

      return {
        ...state,
        filters: keepFilters ? state.filters : initialFilters,
      };
    }
    case REFRESHING_FILTERS:
      return {
        ...state,
        data: {
          ...state.data,
          container: {
            ...state.data.container,
            json_dict: {
              ...state.data.container.json_dict,
              cardlists: action.cardlists,
            },
          },
        },
      };
    case SET_FILTERS:
      return {
        ...state,
        filters: action.filters,
      };
    case SET_IS_INITIALIZING:
      return {
        ...state,
        isInitializing: action.isInitializing,
      };
    default:
      return state;
  }
};

export const fetchMore = (path, i) => async (dispatch, getState) => {
  dispatch({ type: FETCHING_MORE });
  try {
    if (!path) {
      const mores = getState()
        .pagedata.data.container.json_dict.cardlists.map((cardlist, i) => {
          return { i, path: cardlist.more };
        })
        .filter(({ path }) => path);
      return dispatch(fetchMore(mores[0].path, mores[0].i));
    }

    const res = await fetch(`${DOMAIN}/${path}`);
    const data = await res.json();

    // Append new data to existing data
    const { json_dict } = getState().pagedata.data.container;
    const newCardViews = [...json_dict.cardlists[i].cardviews, ...data.cardviews];
    const initCardlists = [
      ...json_dict.cardlists.slice(0, i),
      {
        ...json_dict.cardlists[i],
        cardviews: newCardViews,
        more: data.more,
      },
      ...json_dict.cardlists.slice(i + 1),
    ];

    // If additional filters are set, fetch card data and filter
    const filters = await getState().additionalFilters.filters;
    if (filters) await dispatch(addCards(data.cardviews.map(({ name }) => name)));
    const cardlists = filters
      ? filterCardlists({
          cards: await getState().cards.cards,
          filters,
          initCardlists,
        })
      : initCardlists;

    // Format data for clipboard
    const clipboarddata = flatten(
      cardlists.map((cardlist) => cardlist.cardviews.map((cardview) => cardview.name))
    ).join("\n");

    dispatch({
      json_dict: { cardlists, clipboarddata, initCardlists },
      type: FETCHING_MORE_SUCCESS,
    });
  } catch (error) {
    if (error.name === "AbortError") {
      dispatch({ type: FETCHING_MORE_ABORT });
    } else {
      console.error(error);
      dispatch({ error: error.toString(), type: FETCHING_MORE_ERROR });
    }
  }
};

export const fetchPageData =
  ({ history, location }) =>
  async (dispatch, getState) => {
    const [dir, page, subpage, subsubpage] = location.pathname.slice(1).split("/");

    if (dir === "cards" && !page) {
      return history("/top");
    }

    const query = queryString.parse(location.search);
    query.cmdr = page;
    query.dir = dir;

    // Changing this? Update edhrec/batch/sitemap.py!
    const url = query.f
      ? `/api/filters/?${queryString.stringify(query)}`
      : dir === "deckpreview"
      ? `/api/deckpreview/${page}`
      : dir === "commanders" && !page
      ? `${DOMAIN}/commanders/year.json`
      : dir === "sets" && !page
      ? "https://json.edhrec.com/static/sets"
      : dir === "top" && !page
      ? `${DOMAIN}/top/year.json`
      : page === "gpt"
      ? `${DOMAIN}/sets/g_pt.json`
      : page === "boomerang"
      ? `${DOMAIN}/cards/boomerang_.json`
      : `${DOMAIN}${location.pathname.replace(/\/$/, "")}.json`;

    await getState().pagedata.fetches.map((fetch) => fetch.abort && fetch.abort());
    const myFetch = abortableFetch(url);
    dispatch({ fetch: myFetch, type: FETCHING_PAGEDATA });

    let res;
    try {
      res = await myFetch.ready;
    } catch (error) {
      console.log(error.name);
      if (error.name === "AbortError") {
        return dispatch({ type: FETCHING_PAGEDATA_ABORT });
      } else if (error.name === "TypeError" && subsubpage) {
        // Redirect to subpage if subsubpage doesn't exist
        dispatch(toast("Not enough data, redirected to related page"));
        return history(`/${dir}/${page}/${subpage}`, { replace: true });
      } else {
        return dispatch({
          error: error.toString(),
          type: FETCHING_PAGEDATA_ERROR,
        });
      }
    }

    if (res.status !== 200) {
      if (res.status === 504 && query.f) {
        return dispatch({ type: FETCHING_PAGEDATA_TIMEDOUT_FILTER });
      } else {
        return dispatch({ error: res.status, type: FETCHING_PAGEDATA_ERROR });
      }
    }

    const json = await res.json();
    if (json.redirect) {
      return history(json.redirect, { replace: true });
    }

    let comboHeader = undefined;
    let data = {};
    if (json.container && json.container.json_dict.cardlists) {
      // Combo subpage hacks
      let initCardlists = undefined;
      if (dir === "combos" && subpage) {
        comboHeader = json.container.json_dict.cardlists[0];
        initCardlists = json.container.json_dict.cardlists.slice(1);
      } else if (dir === "combos" && !page) {
        initCardlists = [];
      } else {
        initCardlists = json.container.json_dict.cardlists.map((cardlist) => ({
          ...cardlist,
          cardviews: cardlist.cardviews.map((cardview) => ({
            ...cardview,
            key: v4(),
          })),
          url: v4(),
        }));
      }

      // Fetch card json
      dispatch(
        addCards(
          flatten(
            initCardlists.map((cardlist) =>
              cardlist.cardviews.map((cardview) => cardview.sanitized_wo || cardview.names || cardview.name)
            )
          )
        )
      );

      data = {
        ...json,
        container: {
          ...json.container,
          json_dict: {
            ...json.container.json_dict,
            cardlists: initCardlists,
            clipboarddata: flatten(
              initCardlists.map((cardlist) => cardlist.cardviews.map((cardview) => cardview.name))
            ).join("\n"),
            initCardlists,
          },
        },
      };
    } else {
      data = json;
    }

    if (comboHeader) {
      data.container.comboHeader = comboHeader;
    }

    // TODO port to python
    if (dir === "decks") {
      delete data.description;
    }

    dispatch({
      data,
      type: FETCHING_PAGEDATA_SUCCESS,
    });
  };

export const reloadAds = () => (dispatch) => {
  dispatch({ isInitializing: true, type: SET_IS_INITIALIZING });
  setTimeout(() => dispatch({ isInitializing: false, type: SET_IS_INITIALIZING }));
};

export const setFilters = (filters) => (dispatch) => dispatch({ filters, type: SET_FILTERS });

export const initialize = () => (dispatch) => dispatch({ isInitializing: false, type: SET_IS_INITIALIZING });

export const submitFilters =
  ({ location, history }) =>
  (dispatch, getState) => {
    const { card } = getState().pagedata.data.container.json_dict;
    const { filters } = getState().pagedata;
    const [dir, page] = location.pathname.slice(1).split("/");

    const countQueries = filters.count
      .filter(({ number, type }) => type.length > 0 && number)
      .map(({ number, op, type }) => `${type}:${op}=${number}`)
      .join(";");
    const nameQueries = filters.name
      .filter(({ name }) => name)
      .map(({ name, preposition }) => `${preposition}=${name}`)
      .join(";");

    const sanitized_wo =
      card?.cards && (card.cards[0].legal_partner || card.cards[0].background_chooser)
        ? card.cards.map(({ url }) => url).join("+")
        : card.sanitized_wo;

    if (nameQueries || countQueries) {
      const newQuery = {
        f: nameQueries + (nameQueries && countQueries && ";") + countQueries,
      };
      if (queryString.parse(location.search).f !== newQuery.f) {
        history(`/${dir}/${sanitized_wo}?${queryString.stringify(newQuery)}`);
      }
    }
  };

export default pagedata;
